import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  AlertText,
  Checkbox,
  Field,
  FormLayout,
  inputStyle,
  PortalButton,
  StatusText,
  useApiFieldErrors,
} from "@portal/common";
import {
  ADVANCED_PURCHASE_DATE_WARNING,
  type Advisor,
  type CatalogEntry,
  type MemberInput,
  type MemberUpdateInput,
} from "./api";
import {
  MEMBER_FORM_FIELDS,
  memberChangeReasonSchema,
  memberSchema,
  type MemberFormValues,
} from "./memberSchemas";

export function MemberForm({
  initial,
  editing,
  onSubmit,
  onCancel,
  submitting,
  idTypes = [],
  cities = [],
  advisors = [],
  modal,
  submitError,
  onUnhandledSubmitError,
}: {
  initial?: Partial<MemberInput>;
  editing?: boolean;
  // En edición, teléfono y fecha de compra vacíos viajan como `null`
  // explícito ("borrar"); ausentes, la API los dejaría como estaban.
  onSubmit: (value: MemberUpdateInput) => void;
  onCancel: () => void;
  submitting?: boolean;
  idTypes?: CatalogEntry[];
  cities?: CatalogEntry[];
  advisors?: Advisor[];
  // Alta: el formulario es el diálogo (`FormLayout modal`, revisión en vivo
  // 2026-09-22 punto #4A). Edición: vive inline en la pestaña "Datos
  // generales", sin diálogo — por eso es opcional y lo pasa quien abre el modal.
  modal?: { label: string; width?: number | string; onClose?: () => void };
  // Mismo criterio que `CatalogEntryForm` (trace f4575316-5c75-4c27-83d0-
  // 340db0aafd9f): el 400/409 de la mutación vive en `MembersScreen` (dueño
  // del `useToast`), no acá. `IDENTIFICATION_DUPLICATE`/`EMAIL_DUPLICATE`
  // (RF-MIE-10, `member.service.ts`) ahora identifican el campo
  // (`identification`/`email`) vía `problem.errors[].field`; este formulario
  // los asocia con `applyApiFieldErrors` y, si no puede, se lo devuelve al
  // padre por `onUnhandledSubmitError` para el toast de siempre.
  submitError?: unknown;
  onUnhandledSubmitError?: (error: unknown) => void;
}) {
  // SPEC-202, finding de diseño de la ola 2: `portal.member-data` (y el
  // formulario de alta, admin.jsx:1078-1082) parten el teléfono en un
  // selector de código de país y el número — hoy este editor lo trataba como
  // un único campo libre y omitía el selector. El contrato de
  // `MemberInput.phone` sigue siendo un string único; acá solo se separa
  // visualmente y se recompone al guardar, igual que hace el prototipo
  // (`form.code + ' ' + form.phone`, admin.jsx:917).
  const PHONE_CODES = ["+593", "+57", "+51", "+1"] as const;
  const splitPhone = (value: string) => {
    const match = PHONE_CODES.find((code) => value.startsWith(code));
    return match
      ? { code: match, number: value.slice(match.length).trim() }
      : { code: PHONE_CODES[0], number: value };
  };
  const initialPhone = splitPhone(initial?.phone ?? "");
  const [phoneCode, setPhoneCode] = useState<string>(initialPhone.code);
  const [phoneNumber, setPhoneNumber] = useState(initialPhone.number);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<MemberFormValues>({
    defaultValues: {
      idTypeId: initial?.idTypeId ?? "",
      identification: initial?.identification ?? "",
      legalName: initial?.legalName ?? "",
      email: initial?.email ?? "",
      phone: initial?.phone ?? "",
      cityId: initial?.cityId ?? "",
      advisorId: initial?.advisorId ?? "",
      // SPEC-202, hallazgo de pérdida silenciosa: no hay selector de taller
      // preferido en este editor todavía (agregar uno es alcance nuevo, no
      // de este paso), pero el valor viaja como campo oculto para que una
      // edición que no lo toca no lo desconecte — mismo criterio que
      // `cityId`/`advisorId`.
      preferredWorkshopId: initial?.preferredWorkshopId ?? "",
      purchaseDate: initial?.purchaseDate ?? null,
      inviteOnCreate: initial?.inviteOnCreate ?? false,
      reason: "",
    },
  });
  // Sin número no hay teléfono: antes, vaciar el número dejaba `phone` en
  // el código de país solo ("+593") y ese texto viajaba como teléfono.
  useEffect(() => {
    const number = phoneNumber.trim();
    setValue("phone", number ? `${phoneCode} ${number}` : "");
  }, [phoneCode, phoneNumber, setValue]);
  useApiFieldErrors(
    submitError,
    setError as (
      field: string,
      error: { type: string; message: string },
    ) => void,
    MEMBER_FORM_FIELDS,
    onUnhandledSubmitError,
  );
  const purchaseDate = watch("purchaseDate");
  const inviteOnCreate = watch("inviteOnCreate");
  const email = watch("email");
  const reason = watch("reason");
  const advisorId = watch("advisorId");
  const changesAccessEmail =
    Boolean(editing) && email.trim() !== (initial?.email ?? "").trim();
  const changesPurchaseDate =
    Boolean(editing) && (purchaseDate ?? "") !== (initial?.purchaseDate ?? "");
  const needsChangeReason = changesAccessEmail || changesPurchaseDate;
  const advancesPurchaseDate =
    changesPurchaseDate &&
    Boolean(initial?.purchaseDate) &&
    Boolean(purchaseDate) &&
    purchaseDate! < initial!.purchaseDate!;
  return (
    <FormLayout
      // SPEC-202, finding de diseño de la ola 2: en edición este formulario
      // ahora vive siempre visible dentro de la pestaña "Datos generales"
      // (no un modal aparte, ver `MembersScreen.tsx`); esa pestaña ya rotula
      // el contenido, así que un título propio acá duplicaría texto que el
      // contrato `portal.member-data` no declara. La creación sigue en su
      // propio modal con título ("Nuevo miembro" en el `Modal`, "Registrar
      // miembro" acá — el mismo par eyebrow/título que admin.jsx:1060-1061).
      title={editing ? undefined : "Registrar miembro"}
      // Punto #21 (revisión en vivo 2026-09-22): el eyebrow del prototipo
      // (admin.jsx:1132); inline (edición) no se muestra.
      eyebrow="Nuevo miembro"
      {...(modal ? { modal } : {})}
      onSubmit={handleSubmit((value) => {
        if (needsChangeReason && !(reason ?? "").trim()) {
          setError("reason", {
            type: "required",
            message: "Indica el motivo del cambio",
          });
          return;
        }
        const parsed = (
          needsChangeReason ? memberChangeReasonSchema : memberSchema
        ).safeParse(value);
        if (!parsed.success) {
          for (const issue of parsed.error.issues)
            setError(issue.path[0] as keyof MemberFormValues, {
              message: issue.message,
            });
          return;
        }
        const data = parsed.data;
        onSubmit({
          idTypeId: data.idTypeId,
          identification: data.identification,
          legalName: data.legalName,
          email: data.email,
          inviteOnCreate: data.inviteOnCreate,
          ...(editing
            ? {
                phone: data.phone || null,
                purchaseDate: data.purchaseDate || null,
              }
            : {
                ...(data.phone ? { phone: data.phone } : {}),
                ...(data.purchaseDate
                  ? { purchaseDate: data.purchaseDate }
                  : {}),
              }),
          ...(data.reason ? { reason: data.reason } : {}),
          ...(data.cityId ? { cityId: data.cityId } : {}),
          ...(data.advisorId ? { advisorId: data.advisorId } : {}),
          ...(data.preferredWorkshopId
            ? { preferredWorkshopId: data.preferredWorkshopId }
            : {}),
        });
      })}
      actions={
        <>
          <PortalButton type="button" tone="secondary" onClick={onCancel}>
            Cancelar
          </PortalButton>
          <PortalButton type="submit" disabled={submitting}>
            {/* SPEC-202, finding de diseño de la ola 2: `portal.member-data`
                declara la acción «Guardar cambios» (admin.jsx:920); «Guardar
                miembro» es el rótulo del alta («Nuevo miembro»,
                admin.jsx:1099), un formulario distinto que sigue con su
                propio texto. */}
            {submitting
              ? "Guardando…"
              : editing
                ? "Guardar cambios"
                : "Guardar miembro"}
          </PortalButton>
        </>
      }
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field
          label="Tipo de identificación"
          required
          error={errors.idTypeId?.message}
        >
          <select
            aria-label="Tipo de identificación"
            style={inputStyle}
            {...register("idTypeId")}
          >
            <option value="">Selecciona un tipo</option>
            {idTypes.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {typeof entry.name === "string" ? entry.name : entry.name.es}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Identificación"
          required
          error={errors.identification?.message}
        >
          <input style={inputStyle} {...register("identification")} />
        </Field>
        <Field label="Nombre legal" required error={errors.legalName?.message}>
          <input style={inputStyle} {...register("legalName")} />
        </Field>
        <Field
          label="Correo electrónico"
          required
          error={errors.email?.message}
        >
          <input type="email" style={inputStyle} {...register("email")} />
        </Field>
        <Field label="Teléfono">
          <div style={{ display: "flex", gap: 8 }}>
            <select
              aria-label="Código de país"
              style={{ ...inputStyle, width: 116 }}
              value={phoneCode}
              onChange={(e) => setPhoneCode(e.target.value)}
            >
              {PHONE_CODES.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
            <input
              aria-label="Teléfono"
              style={inputStyle}
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
          </div>
        </Field>
        <input type="hidden" {...register("phone")} />
        {/* QA-MTZ-002 #4 (decisión humana 2026-09-23, se revierte
            PURCHASE-01): SPEC-202 B-010 conserva la fecha de compra en el
            alta y en la edición (ANNEX-002 §2.4). En edición, cambiarla exige
            motivo y adelantarla muestra el aviso de RF-MIE-18 (SPEC-202
            §«Cambio de fecha de compra»); la API lo exige igual
            (`REASON_REQUIRED`) y deja el cambio auditado. */}
        <Field label="Fecha de compra">
          <input type="date" style={inputStyle} {...register("purchaseDate")} />
        </Field>
        <Field label="Ciudad">
          <select
            aria-label="Ciudad"
            style={inputStyle}
            {...register("cityId")}
          >
            <option value="">Sin asignar</option>
            {cities.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {typeof entry.name === "string" ? entry.name : entry.name.es}
              </option>
            ))}
          </select>
        </Field>
        {/* QA-MTZ-002 ronda 3 (#8), decisión humana 2026-10-12: el asesor
            vuelve a ser editable en la ficha. Se había retirado de la edición
            por el prototipo (admin.jsx:895-925), pero era la única vía para
            reasignarlo y el portal es su fuente de verdad (ADR-025). Valor
            controlado: los asesores cargan aparte y un <select> no
            controlado montado antes caía a «Sin asignar» (mismo caso que
            «Oferta enlazada», #1). */}
        <Field label="Asesor asignado">
          <select
            aria-label="Asesor asignado"
            style={inputStyle}
            {...register("advisorId")}
            value={advisorId ?? ""}
          >
            <option value="">Sin asignar</option>
            {advisors.map((advisor) => (
              <option key={advisor.id} value={advisor.id}>
                {advisor.name}
              </option>
            ))}
          </select>
        </Field>
        {/* Sin selector visible todavía (agregar uno es alcance nuevo, ver
            reporte): campo oculto para que el taller preferido que ya tiene
            el miembro sobreviva a una edición que no lo toca — ningún
            cambio visual (`register` sin nodo visible no reintroduce el
            campo en pantalla). */}
        <input type="hidden" {...register("preferredWorkshopId")} />
        {editing && needsChangeReason ? (
          <Field
            label="Motivo del cambio"
            required
            error={errors.reason?.message}
          >
            <input style={inputStyle} {...register("reason")} />
          </Field>
        ) : null}
      </div>
      {!purchaseDate ? (
        <StatusText>
          Sin fecha de compra el miembro no acumula avance. Puedes guardarlo y
          registrarla después.
        </StatusText>
      ) : null}
      {advancesPurchaseDate ? (
        <AlertText>{ADVANCED_PURCHASE_DATE_WARNING}</AlertText>
      ) : null}
      {!editing ? (
        <div>
          <Checkbox
            name="inviteOnCreate"
            checked={inviteOnCreate}
            onChange={(next) =>
              setValue("inviteOnCreate", next, { shouldDirty: true })
            }
            label="Enviar invitación al guardar"
          />
        </div>
      ) : null}
    </FormLayout>
  );
}
